// SavedDocumentViewer.js
//
// Generic saved-document viewer for PI, Group PI, and Bill documents — the
// same job SavedCommercialViewer does for commercials, but parameterized by
// `docType` instead of duplicated three times. All three document types save
// their live-page snapshot the same way (outerHTML frozen into a *SavedDocument
// table), so the fetch/edit/save mechanics here are identical; only the
// endpoints and the URL param name differ per type.
import axios from '../axios';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

const DOC_CONFIG = {
  pi: {
    param: 'piId',
    getUrl: (id) => `/get_pi_saved_document/${id}`,
    saveUrl: (id) => `/save_pi_document/${id}`,
    notFoundLabel: 'PI',
  },
  groupPi: {
    param: 'groupPiId',
    getUrl: (id) => `/get_group_pi_saved_document/${id}`,
    saveUrl: (id) => `/save_group_pi_document/${id}`,
    notFoundLabel: 'Group PI',
  },
  bill: {
    param: 'billId',
    getUrl: (id) => `/get_bill_saved_document/${id}`,
    saveUrl: (id) => `/save_bill_document/${id}`,
    notFoundLabel: 'Bill',
  },
};

const SavedDocumentViewer = ({ docType }) => {
  const config = DOC_CONFIG[docType];
  const params = useParams();
  const docId = params[config.param];

  const [html, setHtml] = useState(undefined);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${window.backendUrl}${config.getUrl(docId)}`)
      .then((res) => setHtml(res.data.htmlContent))
      .catch(() => setHtml(null));
  }, [docId]);

  // Documents saved from the live page carry contenteditable="false" on every
  // field that page allowed editing (PiPDF/GroupPiPDF/BillPDF2's own edit-mode
  // sweep sets it on td/th/span/strong/h1/h2/div indiscriminately). Any element
  // that already carries contenteditable must always be flipped, even when it
  // has element children — left as "false" inside an editable ancestor, the
  // browser treats it as a locked island you cannot type into. This is the
  // same fix already applied to SavedCommercialViewer.
  useEffect(() => {
    if (!containerRef.current) return;

    const targetNodes = containerRef.current.querySelectorAll(
      'td, th, h1, h2, span, strong, div, [contenteditable]'
    );

    targetNodes.forEach((el) => {
      if (
        el.hasAttribute('contenteditable') ||
        el.children.length === 0 ||
        el.tagName === 'TD' ||
        el.tagName === 'TH'
      ) {
        el.setAttribute('contenteditable', editMode ? 'true' : 'false');
      }
    });
  }, [editMode, html]);

  const handleSave = async () => {
    if (!containerRef.current) return;
    setSaving(true);

    const editableElements = containerRef.current.querySelectorAll('[contenteditable]');
    editableElements.forEach((el) => el.removeAttribute('contenteditable'));

    const updatedHtml = containerRef.current.innerHTML;

    try {
      await axios.post(`${window.backendUrl}${config.saveUrl(docId)}`, {
        htmlContent: updatedHtml,
      });
      setEditMode(false);
    } catch (err) {
      console.error('Failed to update saved document:', err);
      editableElements.forEach((el) => el.setAttribute('contenteditable', 'true'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    if (containerRef.current && html) {
      containerRef.current.innerHTML = html;
    }
  };

  if (html === undefined) return null;
  if (html === null) return <p style={{ margin: 20 }}>No saved document found for this {config.notFoundLabel}.</p>;

  return (
    <div style={{ display: 'block', fontSize: '14px', margin: '0px 20px' }}>
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          [contenteditable] {
            outline: none !important;
            background-color: transparent !important;
          }
        }
        .viewer-edit-mode [contenteditable="true"] {
          outline: 1px dashed #3b82f6 !important;
          background-color: rgba(59, 130, 246, 0.05);
          cursor: text;
          min-width: 12px;
        }
        .viewer-edit-mode [contenteditable="true"]:hover {
          background-color: rgba(59, 130, 246, 0.12);
        }
      `}</style>

      <div className="no-print" style={{ marginBottom: '10px' }}>
        <button onClick={() => (editMode ? handleCancel() : setEditMode(true))}>
          {editMode ? 'Normal View' : 'Edit Content'}
        </button>
        {editMode && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ marginLeft: '10px' }}
          >
            {saving ? 'Saving...' : 'Save Document'}
          </button>
        )}
      </div>

      <div
        ref={containerRef}
        className={editMode ? 'viewer-edit-mode' : ''}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

export default SavedDocumentViewer;
